import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import { useUnviewedCount } from '@/hooks/use-notifications'
import { NotificationBanner } from './notification-banner'

interface LayoutProps {
  children: React.ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const { data: unviewedCount } = useUnviewedCount({ enabled: !!user && user.role !== 'new_user' })

  const isActive = (path: string) => location.pathname === path

  const navLinkClass = (path: string) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive(path)
        ? 'bg-brand-teal/10 text-brand-teal'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
    }`

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo + Nav */}
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-8 h-8 bg-brand-teal rounded-lg flex items-center justify-center">
                  {/* FocusPoint target/crosshair logo */}
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    <circle cx="12" cy="12" r="2" fill="currentColor"/>
                    <line x1="12" y1="1" x2="12" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="1" y1="12" x2="5" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="19" y1="12" x2="23" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className="text-xl font-semibold text-gray-900">FocusPoint</span>
              </Link>

              {/* Navigation */}
              {user && user.role !== 'new_user' && (
                <nav className="hidden md:flex items-center gap-1">
                  <Link to="/" className={navLinkClass('/')}>
                    Scorecard
                  </Link>
                  {user.role === 'admin' && (
                    <>
                      <Link to="/admin/metrics" className={navLinkClass('/admin/metrics')}>
                        Metrics
                      </Link>
                      <Link to="/admin/goals" className={navLinkClass('/admin/goals')}>
                        Goals
                      </Link>
                      <Link to="/admin/bonuses" className={navLinkClass('/admin/bonuses')}>
                        Bonuses
                      </Link>
                      <Link to="/admin/users" className={navLinkClass('/admin/users')}>
                        Users
                      </Link>
                      <Link to="/admin/business-areas" className={navLinkClass('/admin/business-areas')}>
                        Areas
                      </Link>
                    </>
                  )}
                </nav>
              )}
            </div>

            {/* User menu */}
            <div className="flex items-center gap-4">
              {user && (
                <>
                  {/* Notification bell icon */}
                  {user.role !== 'new_user' && unviewedCount && unviewedCount > 0 ? (
                    <Link to="/notifications" className="relative p-1.5 text-gray-500 hover:text-gray-700">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                      </svg>
                      <span className="absolute -top-0.5 -right-0.5 bg-brand-teal text-white text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
                        {unviewedCount > 9 ? '9+' : unviewedCount}
                      </span>
                    </Link>
                  ) : null}

                  <div className="flex items-center gap-2">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <span className="hidden sm:block text-sm text-gray-600">{user.name}</span>
                    <span className={`hidden sm:inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'admin' ? 'bg-brand-teal/10 text-brand-teal' :
                      user.role === 'viewer' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {user.role === 'admin' ? 'Admin' : user.role === 'viewer' ? 'Viewer' : 'New'}
                    </span>
                  </div>
                  <button
                    onClick={logout}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Sign Out
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile nav */}
        {user && user.role === 'admin' && (
          <div className="md:hidden border-t border-gray-200 px-4 py-2">
            <nav className="flex gap-2 overflow-x-auto">
              <Link to="/" className={navLinkClass('/')}>Scorecard</Link>
              <Link to="/admin/metrics" className={navLinkClass('/admin/metrics')}>Metrics</Link>
              <Link to="/admin/goals" className={navLinkClass('/admin/goals')}>Goals</Link>
              <Link to="/admin/bonuses" className={navLinkClass('/admin/bonuses')}>Bonuses</Link>
              <Link to="/admin/users" className={navLinkClass('/admin/users')}>Users</Link>
              <Link to="/admin/business-areas" className={navLinkClass('/admin/business-areas')}>Areas</Link>
            </nav>
          </div>
        )}
      </header>

      {/* Notification banner */}
      {user && user.role !== 'new_user' && <NotificationBanner />}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  )
}
